import { Condition, DisplayLogic, Form, FormAnswers, Question, ForEachFieldArrayItem } from '../../../api/types/forms';
import { ValidationRule } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

// --- Logic Evaluation ---

export const evaluateSingleCondition = (condition: Condition, sourceQuestionValue: any): boolean => {
    const { operator, value: conditionValue } = condition;
    if (sourceQuestionValue === undefined || sourceQuestionValue === null) {
        if (operator === 'not_equals' && conditionValue !== null && conditionValue !== undefined) return true;
        if (operator === 'does_not_contain' && conditionValue !== null && conditionValue !== undefined) return true;
        return false;
    }
    // Convert values to strings for robust comparison (handles "123" vs 123)
    const sourceStr = String(sourceQuestionValue);
    const targetStr = String(conditionValue);

    switch (operator) {
        case 'equals': return sourceStr === targetStr;
        case 'not_equals': return sourceStr !== targetStr;
        case 'greater_than': return Number(sourceQuestionValue) > Number(conditionValue);
        case 'less_than': return Number(sourceQuestionValue) < Number(conditionValue);
        case 'contains':
            return Array.isArray(sourceQuestionValue) ? sourceQuestionValue.includes(conditionValue) : sourceStr.includes(targetStr);
        case 'does_not_contain':
            return Array.isArray(sourceQuestionValue) ? !sourceQuestionValue.includes(conditionValue) : !sourceStr.includes(targetStr);
        default: console.warn(`Unsupported operator: ${operator}`); return false;
    }
};

export const evaluateDisplayLogic = (displayLogic: DisplayLogic, formValues: { [key: string]: any }): boolean => {
    if (!displayLogic || displayLogic.condition_type === 'always') return true;
    if (displayLogic.condition_type === 'if') {
        if (!displayLogic.conditions || displayLogic.conditions.length === 0) return true;
        for (const conditionGroup of displayLogic.conditions) {
            let groupResult = true;
            for (const condition of conditionGroup) {
                const sourceQuestionFieldName = `question_${condition.source_question_id}`;
                const sourceQuestionValue = formValues[sourceQuestionFieldName];
                if (!evaluateSingleCondition(condition, sourceQuestionValue)) {
                    groupResult = false; break;
                }
            }
            if (groupResult) return true;
        }
        return false;
    }
    if (displayLogic.condition_type === 'for_each') return true;
    return true;
};

// --- Validation Rules ---

export const buildValidationRules = (question: Question) => {
    const rules: Record<string, ValidationRule<any> | { value: any; message: string } | ((value: any) => boolean | string)> = {};
    if (question.mandatory) rules.required = 'Este campo es obligatorio';
    const qValidation = question.validation;
    if (qValidation) {
        if (qValidation.min_length !== null && qValidation.min_length !== undefined) rules.minLength = { value: qValidation.min_length, message: `Debe tener al menos ${qValidation.min_length} caracteres` };
        if (qValidation.max_length !== null && qValidation.max_length !== undefined) rules.maxLength = { value: qValidation.max_length, message: `Debe tener como máximo ${qValidation.max_length} caracteres` };
        if (qValidation.pattern) try { rules.pattern = { value: new RegExp(qValidation.pattern), message: 'El formato no es válido' }; } catch (e) { console.error("Invalid regex pattern:", qValidation.pattern, e); }
        if (qValidation.min_value !== null && qValidation.min_value !== undefined && ('number' === question.type || 'date' === question.type || 'datetime' === question.type)) rules.min = { value: qValidation.min_value, message: `El valor mínimo es ${qValidation.min_value}` };
        if (qValidation.max_value !== null && qValidation.max_value !== undefined && ('number' === question.type || 'date' === question.type || 'datetime' === question.type)) rules.max = { value: qValidation.max_value, message: `El valor máximo es ${qValidation.max_value}` };
        if (question.type === 'multiselect') {
            const { min_selections, max_selections } = qValidation;
            if (min_selections !== null && min_selections !== undefined && min_selections > 0) {
                rules.validate = { ...(typeof rules.validate === 'object' ? rules.validate : {}), minSelections: (v: string[]) => (v && v.length >= min_selections) || `Debe seleccionar al menos ${min_selections} opciones` };
            }
            if (max_selections !== null && max_selections !== undefined) {
                rules.validate = { ...(typeof rules.validate === 'object' ? rules.validate : {}), maxSelections: (v: string[]) => (v && v.length <= max_selections) || `Puede seleccionar como máximo ${max_selections} opciones` };
            }
        }
    }
    return rules;
};

// --- Initial Values ---

export const getInitialFormValues = (questions: Question[], initialAnswers?: FormAnswers | null) => {

    const defaultValues: { [key: string]: any } = {};
    questions.forEach(q => {
        const fieldName = `question_${q.id}`;
        // Use loose comparison for IDs to handle string vs number mismatches
        // Check both id (legacy stringId) and uuid (backend UUID) for answer lookups
        const existingAnswer = initialAnswers?.find(ans =>
            (String(ans.questionId) === String(q.id) || String(ans.questionId) === String(q.uuid)) &&
            !ans.forEachContextValue
        );

        if (existingAnswer) {
            let value = existingAnswer.value;

            // Parse JSON string values if needed (handles "[15-15-15, DAP]" -> ["15-15-15", "DAP"])
            if (typeof value === 'string' && (value.startsWith('[') || value.startsWith('[[') || value.startsWith('{'))) {
                try {
                    value = JSON.parse(value);
                } catch (e) {
                    console.warn('[getInitialFormValues] Failed to parse JSON value:', value, e);
                }
            }

            // Handle non-JSON array format: "[15-15-15, DAP, 25-4-24, Otro]" (migration legacy format)
            // This is a string that looks like an array but isn't valid JSON
            if (typeof value === 'string' && value.startsWith('[') && value.endsWith(']')) {
                try {
                    // Remove brackets and split by comma
                    const trimmed = value.slice(1, -1); // Remove [ and ]
                    value = trimmed.split(',').map(v => v.trim());
                    console.log('[getInitialFormValues] Parsed legacy array format:', value);
                } catch (e) {
                    console.warn('[getInitialFormValues] Failed to parse legacy array format:', value, e);
                }
            }

            // For multiselect: ensure array
            // Handle both "multiselect" (uppercase) and "multiselect" (lowercase)
            const isMultiselect = q.type === 'multiselect';
            if (isMultiselect) {
                console.log('[getInitialFormValues] Multiselect question:', q.id, 'type:', q.type, 'raw value:', existingAnswer.value, 'parsed value:', value, 'isArray:', Array.isArray(value));
                // Ensure we have an array
                if (!Array.isArray(value)) {
                    value = value ? [value] : [];
                }
                console.log('[getInitialFormValues] Final multiselect value:', value);
            }

            // Ensure file values are always arrays
            if (q.type === 'file' && !Array.isArray(value)) {
                value = value ? [value] : [];
            }
            defaultValues[fieldName] = value;
        } else if (q.default_value !== undefined && q.default_value !== null) {
            defaultValues[fieldName] = q.default_value;
        } else {
            switch (q.type) {
                case 'text': case 'textarea': case 'number': defaultValues[fieldName] = ''; break;
                case 'date': case 'datetime': defaultValues[fieldName] = null; break;
                case 'select': defaultValues[fieldName] = ''; break;
                case 'multiselect': defaultValues[fieldName] = []; break;
                case 'checkbox': defaultValues[fieldName] = false; break;
                case 'radio': defaultValues[fieldName] = ''; break;
                case 'file': defaultValues[fieldName] = []; break;
                default: defaultValues[fieldName] = '';
            }
        }
    });

    return defaultValues;
};

// --- Answers Extraction ---

export const extractForEachAnswers = (
    question: Question,
    fieldArrayData: any[],
    formDefinition: Form,
    answers: FormAnswers,
    parentContextPath: string = '',
    forcedContextValue?: string
) => {
    fieldArrayData.forEach((instanceData) => {
        if (instanceData[question.id] !== undefined) {
            const processedInstanceValue = instanceData[question.id];

            const contextValue = forcedContextValue !== undefined ? forcedContextValue : instanceData._forEachContextValue;

            const answer = {
                answerId: instanceData._answerId || uuidv4(),
                questionId: question.uuid, // Use backend UUID for answer storage
                value: processedInstanceValue,
                forEachContextValue: contextValue,
                parentContextId: `${question.display_logic.source_multiselect_id}_${contextValue}`
            };

            const isEmptyArray = Array.isArray(processedInstanceValue) && processedInstanceValue.length === 0;
            const isEmptyString = typeof processedInstanceValue === 'string' && processedInstanceValue === '';
            const isNullOrUndefined = processedInstanceValue === null || processedInstanceValue === undefined;

            if (!isNullOrUndefined && !isEmptyArray && !isEmptyString) {
                answers.push(answer);
            }
        }

        Object.keys(instanceData).forEach(key => {
            if (key.startsWith('forEachFields_')) {
                const nestedQuestionId = key.replace('forEachFields_', '');
                const nestedQuestion = formDefinition.questions.find(q => q.id === nestedQuestionId);

                if (nestedQuestion && Array.isArray(instanceData[key])) {
                    const contextValue = instanceData._forEachContextValue;
                    const fullContextPath = parentContextPath ? `${parentContextPath}_${contextValue}` : contextValue;
                    extractForEachAnswers(nestedQuestion, instanceData[key], formDefinition, answers, fullContextPath);
                }
            }
        });
    });
};

export const convertFormDataToAnswers = (data: { [key: string]: any }, formDefinition: Form, initialAnswers?: FormAnswers | null): FormAnswers => {

    const answers: FormAnswers = [];

    formDefinition.questions.forEach(q => {
        const shouldDisplay = evaluateDisplayLogic(q.display_logic, data);

        if (shouldDisplay) {
            if (q.display_logic.condition_type === 'for_each' && q.display_logic.source_multiselect_id) {
                const isNestedForEach = formDefinition.questions.some(parentQ =>
                    parentQ.display_logic.condition_type === 'for_each' &&
                    formDefinition.questions.some(childQ =>
                        childQ.display_logic.condition_type === 'for_each' &&
                        (childQ.display_logic.source_multiselect_id === parentQ.id || childQ.display_logic.source_multiselect_id === parentQ.uuid) &&
                        childQ.id === q.id
                    )
                );

                if (!isNestedForEach) {
                    const sourceQId = q.display_logic.source_multiselect_id;
                    // Try to find by id (stringId) first, then by uuid (for backward compatibility)
                    const sourceQ = formDefinition.questions.find(sq => sq.id === sourceQId || sq.uuid === sourceQId);

                    if (sourceQ && sourceQ.type === 'multiselect') {
                        const sourceFieldName = `question_${sourceQ.id}`;
                        const sourceValue = data[sourceFieldName];
                        const selectedValues = Array.isArray(sourceValue) ? sourceValue : [];

                        selectedValues.forEach((val: string) => {
                            const splitKey = `forEachFields_${sourceQ.id}_${val}_${q.id}`;
                            const repeatedQuestionData = data[splitKey] as ForEachFieldArrayItem[] || [];
                            extractForEachAnswers(q, repeatedQuestionData, formDefinition, answers, '', val);
                        });
                    } else {
                        const fieldArrayName = `forEachFields_${q.id}`;
                        const repeatedQuestionData = data[fieldArrayName] as ForEachFieldArrayItem[] || [];
                        extractForEachAnswers(q, repeatedQuestionData, formDefinition, answers);
                    }
                }
            } else {
                const fieldName = `question_${q.id}`;
                let value = data[fieldName];
                if (q.type === 'multiselect' && !Array.isArray(value)) value = [];
                if (q.type === 'file' && !Array.isArray(value)) value = value ? [value] : []; // Ensure file is array

                const processedValue = value;

                if (value !== undefined) {
                    const isEmptyArray = Array.isArray(processedValue) && processedValue.length === 0;
                    const isEmptyString = typeof processedValue === 'string' && processedValue === '';
                    const isValidValue = processedValue !== null && !isEmptyArray && !isEmptyString;

                    if (isValidValue) {
                        // Look up initial answer using both id (legacy) and uuid (backend)
                        const initialAnswer = initialAnswers?.find(ans =>
                            ans.questionId === q.id || ans.questionId === q.uuid
                        );
                        answers.push({
                            answerId: initialAnswer?.answerId || uuidv4(),
                            questionId: q.uuid, // Use backend UUID for answer storage
                            value: processedValue
                        });
                    }
                }
            }
        }
    });

    return answers;
};


// --- Answer Comparison/Normalization ---

export const normalizeAnswers = (answers: FormAnswers): FormAnswers => {
    if (!Array.isArray(answers)) return [];
    // Filter out empty values to ensure comparison ignores irrelevant differences (like null vs undefined vs '' vs [])
    const cleanAnswers = answers.filter(a => {
        const v = a.value;
        if (v === null || v === undefined) return false;
        if (typeof v === 'string' && v.trim() === '') return false;
        if (Array.isArray(v) && v.length === 0) return false;
        return true;
    });


    return cleanAnswers.sort((a, b) => {
        const qCompare = (a.questionId || '').localeCompare(b.questionId || '');
        if (qCompare !== 0) return qCompare;
        return (a.forEachContextValue || '').localeCompare(b.forEachContextValue || '');
    });
};

/**
 * Robust change detection that handles missing values, partial data, and template changes.
 * Treats null/undefined/"" as equivalent "empty" states using question-by-question logic.
 */
export const hasFormChanged = (initialAnswers: FormAnswers | null | undefined, currentAnswers: FormAnswers): boolean => {
    if (!initialAnswers) {
        // If no initial state, check if current has any non-empty answers
        return currentAnswers.some(ans => {
            const v = ans.value;
            return v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
        });
    }

    // Index simple scalar answers by QuestionID + Context
    // Complex answers (arrays, files) might need more care, but stringification usually works for simple arrays
    const toMap = (answers: FormAnswers) => {
        const map = new Map<string, any>();
        answers.forEach(a => {
            const key = `${a.questionId}::${a.forEachContextValue || 'root'}`;
            map.set(key, a.value);
        });
        return map;
    };

    const initialMap = toMap(initialAnswers || []);
    const currentMap = toMap(currentAnswers);

    // 1. Check for modifications or deletions in existing initial answers
    for (const [key, initialVal] of initialMap.entries()) {
        const currentVal = currentMap.get(key);

        const isEmptyInitial = initialVal === null || initialVal === undefined || initialVal === '' || (Array.isArray(initialVal) && initialVal.length === 0);
        const isEmptyCurrent = currentVal === null || currentVal === undefined || currentVal === '' || (Array.isArray(currentVal) && currentVal.length === 0);

        if (isEmptyInitial && isEmptyCurrent) continue; // Both empty = no change

        // Change detected if:
        // - Initial was NOT empty, but Current IS empty (Deletion)
        // - Both exist but differ
        if (!isEmptyInitial && isEmptyCurrent) return true; // Deletion

        if (!isEmptyInitial && !isEmptyCurrent) {
            // Both exist, compare values
            // Use relaxed comparison: convert to string to handle number vs string "123" vs 123
            // For arrays/objects, use JSON stringify but normalizes types inside if possible?
            // Simple approach: JSON.stringify usually suffices if structures match.
            // But for simple scalar mismatches:
            const val1 = initialVal;
            const val2 = currentVal;

            // Direct loose equality check for primitives
            if (val1 == val2) continue; // No change

            // If types differ but might be equivalent (e.g. string "true" vs boolean true?)
            // Or "123" vs 123.
            if (String(val1) === String(val2)) continue; // No change

            // fallback to deep comparison
            const s1 = JSON.stringify(val1);
            const s2 = JSON.stringify(val2);
            if (s1 !== s2) return true;
        }
    }

    // 2. Check for additions (keys in current that are non-empty and weren't in initial)
    for (const [key, currentVal] of currentMap.entries()) {
        if (!initialMap.has(key)) {
            // New entry. Is it non-empty?
            const isEmptyCurrent = currentVal === null || currentVal === undefined || currentVal === '' || (Array.isArray(currentVal) && currentVal.length === 0);
            if (!isEmptyCurrent) return true; // Addition
        }
    }

    return false;
};
